/**
 * CR-016 Báo dự án công trình (DEC-263): CTV, NV kinh doanh, tài xế chụp công trình đang thi công (ảnh, video, vị trí)
 * để KD thị trường tiếp cận. Dùng chung cho API và giao diện. Khoảng cách dùng distanceM (fleet-trip).
 */
export const PROJECT_SITE_STATUSES = ['new', 'approaching', 'quoted', 'won', 'lost', 'duplicate'] as const;
export type ProjectSiteStatus = (typeof PROJECT_SITE_STATUSES)[number];
export const PROJECT_SITE_STATUS_LABELS: Record<ProjectSiteStatus, string> = {
  new: 'Mới báo',
  approaching: 'Đang tiếp cận',
  quoted: 'Đã báo giá',
  won: 'Thành khách',
  lost: 'Không tiếp cận được',
  duplicate: 'Trùng dự án',
};
/** Đổi trạng thái bấm tay (Trùng dự án đi qua thao tác Gộp). Thành khách / Không tiếp cận được mở lại về Đang tiếp cận. */
export const PROJECT_SITE_TRANSITIONS: Record<ProjectSiteStatus, ProjectSiteStatus[]> = {
  new: ['approaching', 'lost'],
  approaching: ['quoted', 'won', 'lost'],
  quoted: ['approaching', 'won', 'lost'],
  won: ['approaching'],
  lost: ['approaching'],
  duplicate: [],
};
export const canTransitionSite = (from: ProjectSiteStatus, to: ProjectSiteStatus) => PROJECT_SITE_TRANSITIONS[from].includes(to);

export const PROJECT_SITE_STAGES = ['foundation', 'structure', 'mep', 'finishing', 'unknown'] as const;
export type ProjectSiteStage = (typeof PROJECT_SITE_STAGES)[number];
export const PROJECT_SITE_STAGE_LABELS: Record<ProjectSiteStage, string> = {
  foundation: 'San lấp, móng',
  structure: 'Dựng khung, kết cấu',
  mep: 'Lắp MEP, PCCC, điện nước',
  finishing: 'Hoàn thiện',
  unknown: 'Chưa rõ',
};

export const PROJECT_SITE_PRODUCT_GROUPS = ['steel', 'fire', 'plastic_pipe', 'electric_water', 'inox'] as const;
export type ProjectSiteProductGroup = (typeof PROJECT_SITE_PRODUCT_GROUPS)[number];
export const PROJECT_SITE_PRODUCT_GROUP_LABELS: Record<ProjectSiteProductGroup, string> = {
  steel: 'Thép hình, ống thép',
  fire: 'Thiết bị, ống PCCC',
  plastic_pipe: 'Ống nhựa',
  electric_water: 'Vật tư điện nước',
  inox: 'Inox',
};

export const PROJECT_SITE_REPORTER_KINDS = ['ctv', 'driver', 'staff'] as const;
export type ProjectSiteReporterKind = (typeof PROJECT_SITE_REPORTER_KINDS)[number];
export const PROJECT_SITE_REPORTER_LABELS: Record<ProjectSiteReporterKind, string> = { ctv: 'CTV', driver: 'Tài xế', staff: 'Nhân viên' };

export const PROJECT_SITE_LOG_KINDS = ['call', 'visit', 'note'] as const;
export type ProjectSiteLogKind = (typeof PROJECT_SITE_LOG_KINDS)[number] | 'status';
export const PROJECT_SITE_LOG_LABELS: Record<ProjectSiteLogKind, string> = {
  call: 'Gọi điện',
  visit: 'Ghé công trường',
  note: 'Ghi chú',
  status: 'Đổi trạng thái',
};

export const PROJECT_SITE_CODE_PREFIX = 'DA';
/** Dự án báo cách nhau dưới bán kính này coi là nghi trùng (gợi ý gộp). */
export const PROJECT_SITE_NEAR_M = 200;
export const PROJECT_SITE_MAX_PHOTOS = 12;
export const PROJECT_SITE_MAX_VIDEOS = 3;
export const PROJECT_SITE_PHOTO_BYTES = 3 * 1024 * 1024;
export const PROJECT_SITE_THUMB_BYTES = 512 * 1024;
export const PROJECT_SITE_VIDEO_BYTES = 8 * 1024 * 1024;
export const PROJECT_SITE_VIDEO_SECONDS = 30;
/** Tài xế: xe chạy nhanh hơn ngưỡng này (km/h, theo GPS điện thoại) thì không mở máy ảnh. */
export const DRIVER_MOVING_KMH = 5;

/** Tên hiển thị: tên công trình, không có thì địa chỉ (tài xế báo nhanh không nhập tên). */
export const siteTitle = (s: { name: string | null; address: string | null; code: string }) => s.name || s.address || `Công trình ${s.code}`;

/**
 * CR-016 B2 (DEC-266): AI đọc ảnh bảng thông tin dự án (bảng treo ở cổng công trường) để gợi ý điền thông tin.
 * Chỉ là gợi ý: KD thị trường xem lại, sửa rồi mới Lưu. Ô nào AI không đọc chắc chắn là null.
 */
export interface ProjectSiteBoardReading {
  name: string | null;
  investor: string | null;
  mainContractor: string | null;
  address: string | null;
  contactName: string | null;
  contactPhone: string | null;
  note: string | null;
}
/** Số ảnh tối đa gửi AI mỗi lần đọc (ảnh báo đầu tiên thường là bảng thông tin). */
export const PROJECT_SITE_BOARD_MAX_PHOTOS = 3;

/**
 * CR-016 B4 (DEC-293). Thưởng báo dự án: không có mức mặc định, GĐ nhập từng dự án trong khoảng dưới đây khi duyệt.
 * DEC-369 (19/10/2026): mọi nhân viên báo dự án đều có thưởng khi dự án thành khách (trước chỉ CTV / tài xế); GĐ chỉ duyệt khi tin báo đủ 4 thứ ở SITE_REWARD_REQUIREMENTS.
 * Hoa hồng CTV: % doanh thu chưa VAT, cài từng CTV (không % mặc định).
 */
export const SITE_REWARD_MIN_VND = 200_000;
export const SITE_REWARD_MAX_VND = 2_000_000;
export const CTV_COMMISSION_MIN_PCT = 2;
export const CTV_COMMISSION_MAX_PCT = 10;

/** 4 thứ tin báo dự án phải có mới được thưởng (DEC-369). */
export const SITE_REWARD_REQUIREMENTS = ['location', 'board', 'photo', 'phone'] as const;
export type SiteRewardRequirement = (typeof SITE_REWARD_REQUIREMENTS)[number];
export const SITE_REWARD_REQUIREMENT_LABELS: Record<SiteRewardRequirement, string> = {
  location: 'Địa điểm',
  board: 'Biển báo công trình',
  photo: 'Hình ảnh',
  phone: 'Số điện thoại',
};
export const SITE_REWARD_REQUIREMENT_HINTS: Record<SiteRewardRequirement, string> = {
  location: 'Vị trí GPS hoặc địa chỉ công trình',
  board: 'Chủ đầu tư hoặc nhà thầu chính, chép từ biển báo công trình',
  photo: 'Ít nhất 1 ảnh công trình',
  phone: 'SĐT người liên hệ ở công trình (từ 9 số)',
};
export interface SiteRewardFacts {
  lat: number | null;
  lng: number | null;
  address: string | null;
  investor: string | null;
  mainContractor: string | null;
  contactPhone: string | null;
  photoCount: number;
}
const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;
/** Những thứ tin báo còn thiếu để được thưởng (rỗng = đủ điều kiện). */
export function siteRewardMissing(f: SiteRewardFacts): SiteRewardRequirement[] {
  const has: Record<SiteRewardRequirement, boolean> = {
    location: (f.lat !== null && f.lng !== null) || filled(f.address),
    board: filled(f.investor) || filled(f.mainContractor),
    photo: f.photoCount > 0,
    phone: (f.contactPhone ?? '').replace(/\D/g, '').length >= 9,
  };
  return SITE_REWARD_REQUIREMENTS.filter((k) => !has[k]);
}

export const SITE_REWARD_STATUSES = ['pending', 'approved', 'paid', 'cancelled'] as const;
export type SiteRewardStatus = (typeof SITE_REWARD_STATUSES)[number];
export const SITE_REWARD_STATUS_LABELS: Record<SiteRewardStatus, string> = {
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt, chờ chi',
  paid: 'Đã chi',
  cancelled: 'Không thưởng',
};
